import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import './AnimatedTestimonials.css';

export interface Testimonial {
  src: string;
  name: string;
  designation: string;
  quote: string;
}

interface AnimatedTestimonialsProps {
  testimonials: Testimonial[];
  autoplay?: boolean;
  autoplayInterval?: number;
}

export const AnimatedTestimonials: React.FC<AnimatedTestimonialsProps> = ({
  testimonials,
  autoplay = true,
  autoplayInterval = 5000,
}) => {
  const [active, setActive] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActive(prev => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  const handlePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActive(prev => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, testimonials.length]);

  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(handleNext, autoplayInterval);
    return () => clearInterval(interval);
  }, [autoplay, autoplayInterval, handleNext]);

  const randomRotations = testimonials.map((_, i) =>
    (((i * 137 + 42) % 21) - 10) // deterministic pseudo-random -10 to +10
  );

  return (
    <div className="at-container">
      <div className="at-inner">
        {/* Image stack */}
        <div className="at-image-col">
          <div className="at-image-stack">
            {testimonials.map((t, idx) => {
              const isActive = idx === active;
              const rotation = randomRotations[idx];
              return (
                <motion.div
                  key={t.src}
                  className="at-image-wrapper"
                  initial={{ opacity: 0, scale: 0.95, rotate: rotation, zIndex: 0 }}
                  animate={{
                    opacity: isActive ? 1 : 0.7,
                    scale: isActive ? 1 : 0.95,
                    rotate: isActive ? 0 : rotation,
                    zIndex: isActive ? testimonials.length : idx,
                    y: isActive ? 0 : 20,
                  }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                >
                  <img
                    src={t.src}
                    alt={t.name}
                    className="at-image"
                    draggable={false}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Text content */}
        <div className="at-text-col">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="at-text-content"
            >
              <h3 className="at-name">{testimonials[active].name}</h3>
              <p className="at-designation">{testimonials[active].designation}</p>
              <p className="at-quote">
                {testimonials[active].quote.split(' ').map((word, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02, duration: 0.2 }}
                    style={{ display: 'inline-block', marginRight: '0.25em' }}
                  >
                    {word}
                  </motion.span>
                ))}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="at-nav">
            <button className="at-nav-btn" onClick={handlePrev} aria-label="Previous">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="at-dots">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  className={`at-dot${idx === active ? ' at-dot--active' : ''}`}
                  onClick={() => { if (!isAnimating) { setIsAnimating(true); setActive(idx); setTimeout(() => setIsAnimating(false), 500); } }}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
            <button className="at-nav-btn" onClick={handleNext} aria-label="Next">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedTestimonials;
